/**
 * Hasonló nevek keresése a pult „Új vendég" ablakához (#168, Karlo §4).
 *
 * MIT VÉD. A pultnál a leggyakoribb hiba nem az elgépelés, hanem a MÁSODIK
 * jegy: a résztvevő nem találja a kódját, a kezelő nem találja a listán
 * (ékezet, fordított névsorrend), és kiad neki egy újat. Az esemény után két
 * jegy áll ugyanazon a néven, a jelenléti szám pedig eggyel többet mutat a
 * valóságnál.
 *
 * MIÉRT KÜLÖN MODUL, ÉS MIÉRT NEM A MODÁLISBAN. Ez az egyetlen darabja a §4-nek,
 * aminek saját szabálya van — a többi űrlap és hálózat. Külön fájlban a szabály
 * önmagában mérhető, a modálistól és a hamis szervertől függetlenül.
 *
 * A SZABÁLY, ÉS AMI SZÁNDÉKOSAN NINCS BENNE:
 *
 * - **Ékezetre és kis/nagybetűre érzéketlen.** „Kovacs Janos" és „Kovács János"
 *   ugyanaz az ember; a pultnál a kezelő gyakran ékezet nélkül gépel.
 * - **A névsorrendre érzéketlen.** A jegyek fele „vezetéknév keresztnév", a
 *   másik fele fordítva érkezik (import, jelentkezés, delegált). Szórendre
 *   érzékeny összevetés a magyar neveken a találatok felét elejtené.
 * - **Elgépelést NEM keres** (nincs szerkesztési távolság). Egy „egy betű
 *   eltérés" szabály a „Nagy Péter" / „Nagy Petra" párt is egyezésnek mondaná
 *   — az pedig KÉT KÜLÖNBÖZŐ ember, és a téves riasztás pontosan azt tanítja
 *   meg a kezelőnek, hogy hagyja figyelmen kívül a figyelmeztetést.
 * - **Puszta vezetéknév-egyezés sem elég**, ugyanezért: egy magyar eseményen a
 *   „Nagy" minden harmadik sorra illeszkedne.
 *
 * Marad a részhalmaz-szabály: a rövidebb névnek MINDEN szava szerepel a
 * hosszabbikban, és legalább két szóból áll. Ez viszi a „Jan van den Berg" =
 * „Jan Berg" esetet is, ami e nélkül kimaradna.
 */

/**
 * ÁTHÚZOTT betűk, amiket az `NFD` NEM bont szét.
 *
 * A kanonikus bontás csak azt a betűt választja szét, amelyiknek a jele
 * KÜLÖNÁLLÓ kombináló karakter (`ć` = `c` + vessző). Az áthúzott betűk viszont
 * önálló kódpontok, saját bontás nélkül — rajtuk a `\p{Diacritic}` nem fog.
 *
 * MIÉRT SZÁMÍT EZ ITT. A `Đ` a SZERB latin ábécé betűje, a szerb pedig ezen a
 * platformon elsőrangú eseménynyelv. Mérve: `„Đorđe Đurić"` → `đorđe/đuric`,
 * `„Dorde Duric"` → `dorde/duric` — a kettő NEM egyezik, tehát a pultnál
 * ékezet nélkül beírt szerb névre a figyelmeztetés NÉMA maradt volna, és a
 * vendég kapott volna egy második jegyet. A `Č`, `Ć`, `Š`, `Ž` rendben van,
 * azok bomlanak.
 *
 * A többi a piac többi nyelvéé: `ł` lengyel, `ø` dán/norvég, `ß`/`æ`/`œ`
 * germán és északi nevekben. Ez SZÁNDÉKOSAN rövid lista — nem az egész
 * Unicode-ot pótoljuk, csak azt, amit egy itteni résztvevőlistán látni lehet.
 */
const STROKED: Record<string, string> = {
  đ: 'd',
  ł: 'l',
  ø: 'o',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
};

/**
 * Egy név összevetésre kész szóhalmaza.
 *
 * `NFD` + a kombináló jelek törlése választja le az ékezetet a betűről; az
 * `\p{Diacritic}` a Unicode saját osztálya, nem egy kézzel írt magyar
 * ékezetlista. Ami ezen ÁTMEGY, azt a {@link STROKED} pótolja — lásd ott, hogy
 * miért nem elég önmagában az `NFD`.
 */
function tokens(value: string): string[] {
  return (
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      // A kisbetűsítés UTÁN, hogy a nagy `Đ` is ide essen.
      .replace(/[đłøßæœ]/g, (ch) => STROKED[ch])
      // Írásjel a névben elválasztó (dr., Kovács-Nagy), nem a szó része.
      .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
      .trim()
      .split(' ')
      .filter((word) => word.length > 0)
  );
}

/** A rövidebb halmaz minden szava szerepel-e a hosszabbikban. */
function isSubset(smaller: string[], larger: string[]): boolean {
  const pool = new Set(larger);
  return smaller.every((word) => pool.has(word));
}

/**
 * A jelöltek közül azok, akiket a beírt név MÁR REGISZTRÁLTKÉNT megmagyarázhat.
 *
 * Generikus, mert a hívó a saját sorát kapja vissza — a pultnak a jegyszám és a
 * „bent van-e" is kell, nem csak a név. A modul ezekről semmit nem tud.
 */
export function findSimilarNames<T extends { name: string }>(full: string, candidates: T[]): T[] {
  const typed = tokens(full);
  // Egyetlen szó még nem név: a kezelő épp gépel. Ilyenkor a figyelmeztetés
  // a fél esemény listáját kiírná, és mire a név kész, már senki nem nézi.
  if (typed.length < 2) return [];

  return candidates.filter((candidate) => {
    const other = tokens(candidate.name);
    if (other.length < 2) return false;
    return typed.length <= other.length ? isSubset(typed, other) : isSubset(other, typed);
  });
}
